'use client';

import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import '@/app/globals.css';
import { MDXEditor, toolbarPlugin, headingsPlugin, listsPlugin, quotePlugin, tablePlugin, thematicBreakPlugin, markdownShortcutPlugin, applyFormat$, currentListType$, applyListType$, insertTable$, convertSelectionToNode$, MDXEditorMethods } from '@mdxeditor/editor';
import { UNDO_COMMAND, REDO_COMMAND, CAN_UNDO_COMMAND, CAN_REDO_COMMAND, COMMAND_PRIORITY_CRITICAL, $getSelection, $isRangeSelection } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ArrowClockwiseIcon, TextHOneIcon, TextItalicIcon, TextBIcon, ArrowCounterClockwiseIcon, ListBulletsIcon, ListNumbersIcon, GridNineIcon, QuotesIcon, TextHTwoIcon, ParagraphIcon, TextHThreeIcon } from '@phosphor-icons/react';
import { usePublisher, useCellValues } from '@mdxeditor/gurx';
import { $createHeadingNode, $createQuoteNode, $isHeadingNode, $isQuoteNode } from '@lexical/rich-text';
import { $createParagraphNode, $isParagraphNode } from 'lexical';
import { mergeRegister } from '@lexical/utils';
import { useDebounce } from 'use-debounce';
import { ErrorBoundary } from 'react-error-boundary';
const iconSize = 22;
const iconWeight = "bold";
const toolbarStyles = "md-toolbar";
const buttonStyles = "md-toolbar-button";
const buttonActiveStyles = "md-toolbar-button-active";
const separatorStyles = "md-toolbar-separator";
const TpUndoRedo = () => {
  const [editor] = useLexicalComposerContext();
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  useEffect(() => {
    return mergeRegister(editor.registerCommand(CAN_UNDO_COMMAND, payload => {
      setCanUndo(payload);
      return false;
    }, COMMAND_PRIORITY_CRITICAL), editor.registerCommand(CAN_REDO_COMMAND, payload => {
      setCanRedo(payload);
      return false;
    }, COMMAND_PRIORITY_CRITICAL));
  }, [editor]);
  return <div className="button-group hidden sm:flex" data-sentry-component="TpUndoRedo" data-sentry-source-file="markdownEditor.tsx">
      <button onClick={() => editor.dispatchCommand(UNDO_COMMAND, undefined)} disabled={!canUndo} className={`${buttonStyles} disabled:opacity-50 disabled:cursor-not-allowed`}>
        <ArrowCounterClockwiseIcon size={iconSize} weight={iconWeight} data-sentry-element="ArrowCounterClockwiseIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => editor.dispatchCommand(REDO_COMMAND, undefined)} disabled={!canRedo} className={`${buttonStyles} disabled:opacity-50 disabled:cursor-not-allowed`}>
        <ArrowClockwiseIcon size={iconSize} weight={iconWeight} data-sentry-element="ArrowClockwiseIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
    </div>;
};
const TpBoldItalicToggles = () => {
  const [editor] = useLexicalComposerContext();
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const applyFormat = usePublisher(applyFormat$);
  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchor = selection.anchor;
          const node = anchor.getNode();
          setIsBold(node.hasFormat('bold' as unknown as never));
          setIsItalic(node.hasFormat('italic' as unknown as never));
        }
      });
    });
  }, [editor]);
  return <div className="button-group" data-sentry-component="TpBoldItalicToggles" data-sentry-source-file="markdownEditor.tsx">
      <button onClick={() => applyFormat('bold')} className={`${buttonStyles} ${isBold ? buttonActiveStyles : ''}`}>
        <TextBIcon size={iconSize} weight={iconWeight} data-sentry-element="TextBIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => applyFormat('italic')} className={`${buttonStyles} ${isItalic ? buttonActiveStyles : ''}`}>
        <TextItalicIcon size={iconSize} weight={iconWeight} data-sentry-element="TextItalicIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
    </div>;
};
const TpBlockTypeSelect = () => {
  const [editor] = useLexicalComposerContext();
  const [currentBlockType, setCurrentBlockType] = useState<string>('paragraph');
  const convertSelectionToNode = usePublisher(convertSelectionToNode$);
  useEffect(() => {
    return editor.registerUpdateListener(() => {
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          const anchor = selection.anchor;
          const node = anchor.getNode();
          const parent = node.getParent();
          if (parent) {
            if ($isHeadingNode(parent)) {
              const tag = parent.getTag();
              setCurrentBlockType(tag);
            } else if ($isQuoteNode(parent)) {
              setCurrentBlockType('quote');
            } else if ($isParagraphNode(parent)) {
              setCurrentBlockType('paragraph');
            }
          }
        }
      });
    });
  }, [editor]);
  const applyFormat = (blockType: string) => {
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        switch (blockType) {
          case 'h1':
          case 'h2':
          case 'h3':
            convertSelectionToNode(() => $createHeadingNode(blockType));
            break;
          case 'paragraph':
            convertSelectionToNode(() => $createParagraphNode());
            break;
          case 'quote':
            convertSelectionToNode(() => $createQuoteNode());
            break;
          default:
            throw new Error(`Unknown block type: ${blockType}`);
        }
      }
    });
  };
  return <div className="button-group" data-sentry-component="TpBlockTypeSelect" data-sentry-source-file="markdownEditor.tsx">
      <button onClick={() => applyFormat('h1')} className={`${buttonStyles} ${currentBlockType === 'h1' ? buttonActiveStyles : ''}`}>
        <TextHOneIcon size={iconSize} weight={iconWeight} data-sentry-element="TextHOneIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => applyFormat('h2')} className={`${buttonStyles} ${currentBlockType === 'h2' ? buttonActiveStyles : ''}`}>
        <TextHTwoIcon size={iconSize} weight={iconWeight} data-sentry-element="TextHTwoIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => applyFormat('h3')} className={`${buttonStyles} ${currentBlockType === 'h3' ? buttonActiveStyles : ''}`}>
        <TextHThreeIcon size={iconSize} weight={iconWeight} data-sentry-element="TextHThreeIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => applyFormat('paragraph')} className={`${buttonStyles} ${currentBlockType === 'paragraph' ? buttonActiveStyles : ''}`}>
        <ParagraphIcon size={iconSize} weight={iconWeight} data-sentry-element="ParagraphIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => applyFormat('quote')} className={`hidden sm:block ${buttonStyles} ${currentBlockType === 'quote' ? buttonActiveStyles : ''}`}>
        <QuotesIcon size={iconSize} weight={iconWeight} data-sentry-element="QuotesIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
    </div>;
};

// const TpCreateLink = () => {
//   const [editor] = useLexicalComposerContext();
//   const [url, setUrl] = useState('');
//   const [title, setTitle] = useState('');
//   const [linkText, setLinkText] = useState('');
//   const [open, setOpen] = useState(false);
//   const [isInLink, setIsInLink] = useState(false);
//   const [currentLink, setCurrentLink] = useState<{ url: string; title: string; text: string } | null>(null);

//   useEffect(() => {
//     return editor.registerUpdateListener(() => {
//       editor.getEditorState().read(() => {
//         const selection = $getSelection();
//         if ($isRangeSelection(selection)) {
//           const node = selection.anchor.getNode();
//           const parent = node.getParent();
//           if (parent && $isLinkNode(parent)) {
//             setIsInLink(true);
//             setCurrentLink({
//               url: parent.getURL(),
//               title: parent.getTitle() || '',
//               text: parent.getTextContent()
//             });
//           } else {
//             setIsInLink(false);
//             setCurrentLink(null);
//           }
//         }
//       });
//     });
//   }, [editor]);

//   const handleCreateLink = () => {
//     if (url && linkText) {
//       editor.update(() => {
//         const selection = $getSelection();
//         if ($isRangeSelection(selection)) {
//           if (isInLink && currentLink) {
//             // Update existing link
//             const node = selection.anchor.getNode();
//             const parent = node.getParent();
//             if (parent && $isLinkNode(parent)) {
//               parent.setURL(url);
//               parent.setTitle(title);
//               const textNode = $createTextNode(linkText);
//               parent.clear();
//               parent.append(textNode);
//             }
//           } else {
//             // Create new link
//             const linkNode = $createLinkNode(url, { title });
//             const textNode = $createTextNode(linkText);
//             linkNode.append(textNode);
//             selection.insertNodes([linkNode]);
//           }
//         }
//       });
//       setUrl('');
//       setTitle('');
//       setLinkText('');
//       setOpen(false);
//     }
//   };

//   const handleOpenChange = (newOpen: boolean) => {
//     if (newOpen && isInLink && currentLink) {
//       setUrl(currentLink.url);
//       setTitle(currentLink.title);
//       setLinkText(currentLink.text);
//     } else {
//       setUrl('');
//       setTitle('');
//       setLinkText('');
//     }
//     setOpen(newOpen);
//   };

//   return (
//     <Popover 
//       isOpen={open} 
//       onOpenChange={handleOpenChange}
//       placement="bottom-start"
//       radius="md"
//       classNames={{
//         base: "box-border w-fit p-0 m-0",
//         content: "box-border flex flex-col gap-4 items-start justify-start w-fit p-4 m-0 bg-gray-800 border border-gray-700 rounded-md",
//       }}
//     >
//       <PopoverTrigger>
//         <button className={`hidden sm:block ${buttonStyles} ${isInLink ? buttonActiveStyles : ''}`}>
//           <LinkIcon size={iconSize} weight={iconWeight} />
//         </button>
//       </PopoverTrigger>
//       <PopoverContent>
//         <div className="flex flex-col gap-4">
//           <div className="flex flex-col gap-2 box-border w-full">
//             <label htmlFor="linkText" className="text-sm text-gray-200">Link Text</label>
//             <Input
//               id="linkText"
//               value={linkText}
//               onChange={(e) => setLinkText(e.target.value)}
//               placeholder="Enter text for link"
//               classNames={{
//                 base: "box-border w-full",
//                 inputWrapper: "box-border w-full rounded-md",
//               }}
//             />
//           </div>
//           <div className="flex flex-col gap-2">
//             <label htmlFor="url" className="text-sm text-gray-200">URL</label>
//             <Input
//               id="url"
//               value={url}
//               onChange={(e) => setUrl(e.target.value)}
//               placeholder="https://example.com"
//               classNames={{
//                 base: "box-border w-full",
//                 inputWrapper: "box-border w-full rounded-md",
//               }}
//             />
//           </div>
//           <div className="flex gap-2">
//             <Button 
//               onPress={handleCreateLink}
//               className="flex-1 bg-gray-400 cursor-pointer"
//             >
//               {isInLink ? 'Update Link' : 'Create Link'}
//             </Button>
//             {isInLink && (
//               <Button 
//                 onPress={() => {
//                   editor.update(() => {
//                     const selection = $getSelection();
//                     if ($isRangeSelection(selection)) {
//                       const node = selection.anchor.getNode();
//                       const parent = node.getParent();
//                       if (parent && $isLinkNode(parent)) {
//                         parent.remove();
//                       }
//                     }
//                   });
//                   setOpen(false);
//                 }}
//                 className="flex-1 bg-red-600 hover:bg-red-700"
//               >
//                 Remove Link
//               </Button>
//             )}
//           </div>
//         </div>
//       </PopoverContent>
//     </Popover>
//   );
// };

const TpListsToggle = () => {
  const [currentListType] = useCellValues(currentListType$);
  const applyListType = usePublisher(applyListType$);
  return <div className="button-group" data-sentry-component="TpListsToggle" data-sentry-source-file="markdownEditor.tsx">
      <button onClick={() => applyListType('bullet')} className={`${buttonStyles} ${currentListType === 'bullet' ? buttonActiveStyles : ''}`}>
        <ListBulletsIcon size={iconSize} weight={iconWeight} data-sentry-element="ListBulletsIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
      <button onClick={() => applyListType('number')} className={`${buttonStyles} ${currentListType === 'number' ? buttonActiveStyles : ''}`}>
        <ListNumbersIcon size={iconSize} weight={iconWeight} data-sentry-element="ListNumbersIcon" data-sentry-source-file="markdownEditor.tsx" />
      </button>
    </div>;
};
const TpInsertTable = () => {
  const insertTable = usePublisher(insertTable$);
  const [isDisabled, setIsDisabled] = useState(false);
  const [editor] = useLexicalComposerContext();
  useEffect(() => {
    const checkIfInsideTable = () => {
      const rootElement = editor.getRootElement();
      const parentNodeName = rootElement?.parentNode?.nodeName.toLowerCase();
      setIsDisabled(['td', 'th'].includes(parentNodeName ?? ''));
    };
    editor.registerUpdateListener(() => {
      checkIfInsideTable();
    });
  }, [editor]);
  return <button onClick={() => insertTable({
    rows: 3,
    columns: 3
  })} disabled={isDisabled} className={`hidden sm:block ${buttonStyles} disabled:opacity-50 disabled:cursor-not-allowed`} data-sentry-component="TpInsertTable" data-sentry-source-file="markdownEditor.tsx">
      <GridNineIcon size={iconSize} weight={iconWeight} data-sentry-element="GridNineIcon" data-sentry-source-file="markdownEditor.tsx" />
    </button>;
};
const Toolbar = () => <div className="button-group" data-sentry-component="Toolbar" data-sentry-source-file="markdownEditor.tsx">
    <TpUndoRedo data-sentry-element="TpUndoRedo" data-sentry-source-file="markdownEditor.tsx" />
    <div className={`${separatorStyles} hidden sm:flex`}></div>
    <TpBoldItalicToggles data-sentry-element="TpBoldItalicToggles" data-sentry-source-file="markdownEditor.tsx" />
    <div className={`${separatorStyles} hidden sm:flex`}></div>
    <TpBlockTypeSelect data-sentry-element="TpBlockTypeSelect" data-sentry-source-file="markdownEditor.tsx" />
    {/* <div className={`${separatorStyles} hidden sm:flex`}></div>
     <TpCreateLink />*/}
    <div className={`${separatorStyles} hidden sm:flex`}></div> 
    <TpListsToggle data-sentry-element="TpListsToggle" data-sentry-source-file="markdownEditor.tsx" />
    <div className={`${separatorStyles} hidden sm:flex`}></div>
    <TpInsertTable data-sentry-element="TpInsertTable" data-sentry-source-file="markdownEditor.tsx" />
  </div>;
interface MarkdownEditorProps {
  markdown: string;
  onChange: (markdown: string) => void;
  onBlurIn?: () => void;
  debounceMs?: number;
  id?: string;
}
const MarkdownEditor = ({
  markdown,
  onChange,
  onBlurIn,
  debounceMs = 0,
  id
}: MarkdownEditorProps) => {
  const [internalMarkdown, setInternalMarkdown] = useState(markdown);
  const editorRef = useRef<MDXEditorMethods>(null);
  const [internalMarkdownDebounced] = useDebounce(internalMarkdown, debounceMs);
  useEffect(() => {
    if (internalMarkdown.toString() !== markdown.toString()) {
      onChange(internalMarkdownDebounced);
    }
  }, [internalMarkdownDebounced]);

  // Update internal markdown when prop changes
  useEffect(() => {
    if (internalMarkdown.toString() !== markdown.toString()) {
      setInternalMarkdown(markdown);
      handleChange(markdown);
      editorRef.current?.setMarkdown(markdown);
    }
  }, [markdown]);
  const handleChange = (newMarkdown: string) => {
    if (newMarkdown.toString() !== internalMarkdown.toString() && editorRef.current) {
      setInternalMarkdown(newMarkdown);
    }
  };
  const handleBlur = () => {
    setTimeout(() => {
      !!onBlurIn ? onBlurIn() : () => {};
    }, debounceMs + 1);
  };
  return <ErrorBoundary fallback={<div>{markdown}</div>} data-sentry-element="ErrorBoundary" data-sentry-component="MarkdownEditor" data-sentry-source-file="markdownEditor.tsx">
      <div id={id}>
        <MDXEditor ref={editorRef} onBlur={handleBlur} className="box-border w-full relative" markdown={internalMarkdownDebounced} spellCheck={true} onChange={handleChange} contentEditableClassName={`prose dark:prose-invert max-w-none max-h-[300px] overflow-y-auto custom-scrollbar2 mr-0`} plugins={[toolbarPlugin({
        toolbarContents: () => <Toolbar />,
        toolbarClassName: toolbarStyles
      }), headingsPlugin(), listsPlugin({
        syntax: true,
        modifySchema: true
      }), quotePlugin(),
      // linkPlugin(),
      // linkDialogPlugin(),
      tablePlugin(), thematicBreakPlugin(), markdownShortcutPlugin()]} data-sentry-element="MDXEditor" data-sentry-source-file="markdownEditor.tsx" />
      </div>
    </ErrorBoundary>;
};
export default MarkdownEditor;